import React, { FC } from 'react';
import { useTranslation } from 'next-i18next';

import { REVIEWS_ROOT_ID } from 'src/listing/constants';
import StarIcon from '../../../common/components/StarIcon';
import ReviewCategory from '../ReviewCategory';

import styles from './Reviews.module.scss';

type RatingAggregates = {
  rating_average?: number;
  listing_accuracy_average?: number;
  departure_and_return_average?: number;
  vessel_and_equipment_average?: number;
  communication_average?: number;
  value_average?: number;
  itinerary_and_experience_average?: number;
};

export type ReviewsProps = {
  totalReviews: number;
  ratingAggregates: RatingAggregates;
};

const Reviews: FC<ReviewsProps> = ({
  ratingAggregates,
  totalReviews,
}) => {
  const [t] = useTranslation('listing-page');

  if (!totalReviews) {
    return null;
  }

  const averageRating = ratingAggregates.rating_average;
  const aggregates = [{
    title: t('reviews--listingAccuracy'),
    rating: ratingAggregates.listing_accuracy_average,
  }, {
    title: t('reviews--departure-return'),
    rating: ratingAggregates.departure_and_return_average,
  }, {
    title: t('reviews--vessel-equipment'),
    rating: ratingAggregates.vessel_and_equipment_average,
  }, {
    title: t('reviews--communication'),
    rating: ratingAggregates.communication_average,
  }, {
    title: t('reviews--value'),
    rating: ratingAggregates.value_average,
  }, {
    title: t('reviews--itinerary-experience'),
    rating: ratingAggregates.itinerary_and_experience_average,
  }].filter((aggregate) => aggregate.rating);

  return (
    <div
      className={styles.root}
      id={REVIEWS_ROOT_ID}
    >
      <h3 className={styles.title}>
        {t('reviews--title')}
      </h3>

      <div className={styles.averages}>
        <StarIcon size="medium" theme="dark" />
        <span className={styles.rating}>{`${averageRating?.toFixed(1)} `}</span>
        <span className={styles.numberOfReviews}>
          {`(${t('reviews--count', { count: totalReviews })})`}
        </span>
      </div>

      {aggregates.length > 0 && (
        <ul className={styles.breakdown}>
          {aggregates.map((aggregate) => (
            <ReviewCategory
              key={aggregate.title}
              title={aggregate.title}
              rating={Math.round(aggregate.rating ?? 0)}
            />
          ))}
        </ul>
      )}
    </div>
  );
};

export default Reviews;
